// Shared collapsible sidebar used by all three apps (web, admin, hub).
// Owns the chrome (mobile drawer, desktop collapse/expand, active-item styling,
// animation) — apps own their content (items, icons, extra slots) via props so
// each app's particular behavior (startup switcher, role-gated items, trailing
// arrows...) stays in the app layer instead of leaking into this component.
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Tooltip } from './Tooltip';

export interface SidebarNavItem {
  id: string;
  label: string;
  icon: ReactNode;
  path: string;
  end?: boolean; // NavLink 'end' matching — omit for prefix matching (the default everywhere today)
  dividerAfter?: boolean;
}

export interface SidebarProps {
  /** Mobile drawer open state — unrelated to the desktop collapsed/expanded state. */
  isOpen: boolean;
  onClose: () => void;
  items: SidebarNavItem[];
  /** localStorage key this instance persists its collapsed state under (one per app). */
  storageKey: string;
  /** Rendered below the top bar, expanded-state only — e.g. web's startup switcher + invite button. */
  topContent?: ReactNode;
  /** Rendered inside the scrollable nav, right after the items, mobile-only (scrolls with the list — e.g. web's profile link). */
  mobileInlineFooter?: ReactNode;
  /** Rendered outside the scrollable nav, pinned at the bottom of the drawer, mobile-only (matches each app's existing pinned logout). */
  mobileFooter?: ReactNode;
  /**
   * Pinned at the bottom of the sidebar on every breakpoint — e.g. web's Chrome extension CTA.
   * Receives the desktop collapsed state so the slot can render its own compact (icon-only)
   * variant with the same `lg:` responsive rules the nav items use.
   */
  bottomContent?: (collapsed: boolean) => ReactNode;
  /** Renders a trailing chevron on every item while expanded — used by the admin sidebar. */
  showTrailingArrow?: boolean;
  /** Wraps a single item's link, e.g. to attach an app-specific description tooltip. */
  itemWrapper?: (item: SidebarNavItem, node: ReactNode) => ReactNode;
  collapseLabel?: string;
  expandLabel?: string;
  closeLabel?: string;
  className?: string;
}

// Standard "sidebar collapse" glyph (the lucide panel-left-close / panel-left-open pair):
// a panel outline with a rail on the left plus an arrow pointing the way the toggle moves —
// left (close) while expanded, right (open) while collapsed. Drawn inline because the repo
// has no icon-library dependency; all sidebar chrome icons are hand-rolled SVGs.
function PanelLeftIcon({ className, collapsed }: { className: string; collapsed: boolean }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d={collapsed ? 'M14 9l3 3-3 3' : 'M16 15l-3-3 3-3'} />
    </svg>
  );
}

function CloseIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function TrailingArrowIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 8 14" fill="none">
      <path
        d="M0.75 0.75L6.75 6.75L0.75 12.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Sidebar({
  isOpen,
  onClose,
  items,
  storageKey,
  topContent,
  mobileInlineFooter,
  mobileFooter,
  bottomContent,
  showTrailingArrow = false,
  itemWrapper,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
  closeLabel = 'Close menu',
  className = '',
}: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? '1' : '0');
    } catch {
      // Private browsing / disabled storage — collapsed state just won't persist across reloads.
    }
  }, [collapsed, storageKey]);

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}

      <aside
        data-theme="dark"
        aria-label="Sidebar"
        className={`
          fixed top-0 right-0 h-full w-60 bg-[#1E1E1E] text-white z-50 shadow-xl flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          lg:translate-x-0 lg:static lg:shadow-none lg:h-full lg:shrink-0
          lg:transition-[width] lg:duration-300 lg:ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:lg:transition-none
          ${collapsed ? 'lg:w-20' : 'lg:w-60'}
          ${className}
        `}
      >
        <div className="flex flex-col h-full min-h-0">
          {/* Top bar — mobile close (X) and desktop collapse chevron share the same top-right slot */}
          <div
            className={`flex items-center justify-end p-4 shrink-0 ${collapsed ? 'lg:justify-center lg:px-2' : ''}`}
          >
            <button
              type="button"
              className="btn btn-circle btn-ghost btn-sm lg:hidden"
              onClick={onClose}
              aria-label={closeLabel}
            >
              <CloseIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`btn btn-circle btn-ghost hidden lg:inline-flex ${
                collapsed ? 'w-10 h-10 min-h-0 p-0' : 'btn-sm'
              }`}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? expandLabel : collapseLabel}
            >
              <PanelLeftIcon className="w-5 h-5" collapsed={collapsed} />
            </button>
          </div>

          {topContent && (
            <div
              className={`grid-rows-[1fr] opacity-100 transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none grid ${
                collapsed ? 'lg:overflow-hidden lg:grid-rows-[0fr] lg:opacity-0' : ''
              }`}
            >
              <div className="min-h-0 min-w-0">{topContent}</div>
            </div>
          )}

          {/* Nav items — scrolls independently so a pinned mobileFooter stays put */}
          <nav
            className={`flex-1 px-4 py-6 overflow-y-auto min-h-0 ${collapsed ? 'lg:px-1.5' : 'lg:px-3'}`}
          >
            <ul className="space-y-2">
              {items.map((item, idx) => {
                const link = (
                  <NavLink
                    to={item.path}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) => `
                      group relative flex items-center gap-3 w-full justify-between px-4 py-3 rounded-lg text-left
                      hover:bg-white/5 transition-all duration-300 ease-out motion-reduce:transition-none
                      ${isActive ? 'bg-[#19A2F8]/20 text-[#19A2F8]' : ''}
                      ${collapsed ? 'lg:w-10 lg:h-10 lg:mx-auto lg:justify-center lg:gap-0 lg:rounded-full lg:px-0 lg:py-0' : ''}
                    `}
                  >
                    <span
                      className={`flex items-center gap-3 min-w-0 ${collapsed ? 'lg:gap-0' : ''}`}
                    >
                      <span className="w-4 h-4 shrink-0 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
                        {item.icon}
                      </span>
                      <span
                        className={`grid transition-[grid-template-columns] duration-300 ease-out motion-reduce:transition-none grid-cols-[1fr] ${
                          collapsed ? 'lg:grid-cols-[0fr]' : ''
                        }`}
                      >
                        <span
                          className="overflow-hidden"
                          style={{ transitionDelay: collapsed ? '0ms' : `${idx * 25}ms` }}
                        >
                          <span
                            className={`font-medium inline-block whitespace-nowrap opacity-100 transition-opacity duration-200 motion-reduce:transition-none ${
                              collapsed ? 'lg:opacity-0' : ''
                            }`}
                          >
                            {item.label}
                          </span>
                        </span>
                      </span>
                    </span>

                    {showTrailingArrow && (
                      <TrailingArrowIcon
                        className={`w-2 h-3.5 ml-auto shrink-0 opacity-50 transition-all duration-200 motion-reduce:transition-none ${
                          collapsed ? 'lg:hidden lg:opacity-0' : ''
                        }`}
                      />
                    )}
                  </NavLink>
                );

                const wrapped = itemWrapper ? itemWrapper(item, link) : link;

                return (
                  <li key={item.id}>
                    {collapsed ? (
                      // Tooltip's own popup is `hidden lg:block`, so this is inert on
                      // mobile (where the collapse feature doesn't apply) without needing
                      // a separate non-tooltip render path.
                      <Tooltip content={item.label} placement="right" delay={200}>
                        {wrapped}
                      </Tooltip>
                    ) : (
                      wrapped
                    )}
                    {item.dividerAfter && <hr className="border-white/10 my-2" />}
                  </li>
                );
              })}
            </ul>

            {mobileInlineFooter && (
              <div className="lg:hidden mt-2 space-y-1">{mobileInlineFooter}</div>
            )}
          </nav>

          {bottomContent && (
            <div className={`shrink-0 px-4 pb-4 ${collapsed ? 'lg:px-1.5' : 'lg:px-3'}`}>
              {bottomContent(collapsed)}
            </div>
          )}

          {mobileFooter && <div className="lg:hidden shrink-0">{mobileFooter}</div>}
        </div>
      </aside>
    </>
  );
}
